import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/apiClient";
import { STATUS_META, formatDate } from "@/lib/status";
import { toast } from "sonner";
import { History, Wrench, Wind, QrCode, Loader2, ExternalLink } from "lucide-react";

export default function CustomerHistoryDialog({ customer, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setLoading(true);
      api.get(`/customers/${customer.id}/history`)
        .then(({ data }) => setData(data))
        .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar histórico"))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, customer]);

  const qrLink = (token) => `${window.location.origin}/eq/${token}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-[#1e7be0]" /> Histórico · {customer?.name}</DialogTitle>
          <DialogDescription>Chamados e equipamentos vinculados a este cliente.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : data ? (
          <div className="space-y-5">
            <div>
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Wrench className="h-4 w-4 text-[#1e7be0]" /> Chamados ({data.requests.length})</h4>
              {data.requests.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="history-requests-empty">Nenhum chamado.</p>
              ) : (
                <div className="space-y-2" data-testid="history-requests-list">
                  {data.requests.map((r) => {
                    const meta = STATUS_META[r.status] || { label: r.status, color: "bg-slate-100 text-slate-700" };
                    return (
                      <div key={r.id} className="rounded-lg border border-slate-200 p-2.5 flex items-center justify-between gap-2" data-testid={`history-request-${r.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">#{r.ticket_number} · {r.service_type}{r.equipment ? ` · ${r.equipment}` : ""}</p>
                          <p className="text-xs text-slate-500">{formatDate(r.created_at)}{r.assigned_technician_name ? ` · ${r.assigned_technician_name}` : ""}</p>
                        </div>
                        <Badge className={`${meta.color} border text-[11px] flex-shrink-0`}>{meta.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Wind className="h-4 w-4 text-[#1e7be0]" /> Equipamentos ({data.equipment.length})</h4>
              {data.equipment.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="history-equipment-empty">Nenhum equipamento.</p>
              ) : (
                <div className="space-y-2" data-testid="history-equipment-list">
                  {data.equipment.map((eq) => (
                    <div key={eq.id} className="rounded-lg border border-slate-200 p-2.5 flex items-center justify-between gap-2" data-testid={`history-equipment-${eq.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{eq.equipment_type}{eq.brand ? ` · ${eq.brand}` : ""}{eq.model ? ` ${eq.model}` : ""}</p>
                        {eq.serial_number && <p className="text-xs text-slate-500">Série: {eq.serial_number}</p>}
                      </div>
                      {eq.qr_token && (
                        <a href={qrLink(eq.qr_token)} target="_blank" rel="noreferrer" className="text-[#1e7be0] flex items-center gap-1 text-xs flex-shrink-0" data-testid={`history-equipment-qr-${eq.id}`}>
                          <QrCode className="h-3.5 w-3.5" /> QR <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
